/* eslint-disable generator-star-spacing */
/* eslint space-before-function-paren: "off" */
import { toast } from 'react-toastify'
import type { SagaIterator } from 'redux-saga'
import { call, delay, put, select, takeLatest } from 'redux-saga/effects'
import { UpdateRequest } from '~api/types'
import { Punchlist } from '../../api'
import { errorTextTryingTo, isEmpty } from '../../helpers'
import { estimateActions, estimatesActions } from '../actions'
import { getEstimateId } from '../selectors'
import { EstimateItem } from '../types'
import { AssignMultipleEstimatesAction, DownloadCSVEstimateAction, estimatesTypes, FetchEstimatesActions, FetchRelatedEstimatesAction, GetCSVEstimateAction, ReleaseEstimateAction, UpdateEstimateActions } from './types'

export function* fetchEstimates({ payload, callback }: FetchEstimatesActions): SagaIterator {
  let success = false
  try {
    const page = payload.page ?? 0
    const size = payload.size ?? 50

    const start = page * size
    const limit = size;

    const searchParams = payload.searchParams ?? ''
    const estimates = yield call(Punchlist.estimates.getEstimates, { start, limit, searchParams })

    if (estimates) {
      yield put(estimatesActions.setEstimates(estimates))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the estimates'))
    if (callback) yield call(callback, false)
  }
}

export function* fetchRelatedEstimates({ payload, callback }: FetchRelatedEstimatesAction): SagaIterator {
  let success = false
  try {
    const estimates = yield call(Punchlist.estimates.getRelatedEstimates, { id: payload })

    if (estimates) {
      yield put(estimatesActions.setRelatedEstimates(estimates))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the related estimates'))
    if (callback) yield call(callback, false)
  }
}

export function* updateEstimate({ payload, callback }: UpdateEstimateActions): SagaIterator {
  let success = false

  try {
    const request: UpdateRequest = []
    const estimateId = yield select(getEstimateId())
    const { id, ...payloadWithoutId } = payload

    for (const attr in payloadWithoutId) {
      request.push({
        op: 'add',
        path: '/' + attr,
        value: payload[attr as keyof EstimateItem],
      })
    }

    const estimate = yield call(
      Punchlist.estimates.patchEstimate,
      payload.id || estimateId,
      request
    )

    if (!isEmpty(estimate)) {
      // yield put(jobActions.fetchJob(jobId))
      yield put(estimateActions.fetchEstimate(estimate.id))
      //yield put(jobActions.fetchHistoryTask(jobId))
      success = true
    }
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('update the estimate'))
    if (callback) yield call(callback, false)
  }
}

export function* fetchEstimatesCounts({ payload, callback }: FetchEstimatesActions): SagaIterator {
  let success = false
  try {
    const searchParams = payload.searchParams ?? ''
    const counts = yield call(Punchlist.estimates.getEstimatesCounts, { searchParams })

    if (counts) {
      yield put(estimatesActions.setEstimatesCounts(counts))
    }
    success = true
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('get the estimates counts'))
    if (callback) yield call(callback, false)
  }
}

export function* releaseEstimate({ payload, callback }: ReleaseEstimateAction): SagaIterator {
  let success = false
  try {
    const {
      estimateId,
      releaseEstimateRequest
    } = payload
    const estimate = yield call(Punchlist.estimates.releaseEstimate, estimateId, releaseEstimateRequest)

    if (!isEmpty(estimate)) {
      yield put(estimateActions.setEstimate(estimate))
      success = true
    }
    if (callback) yield call(callback, success)
  } catch (error) {
    yield call(toast.error, errorTextTryingTo('release the estimate'))
    if (callback) yield call(callback, false)
  }
}

export function* downloadCSVEstimate({ payload, callback }: DownloadCSVEstimateAction): SagaIterator {
  const { searchParams } = payload
  try {
    const csvId = yield call(Punchlist.estimates.requestCSV, searchParams)
    if (csvId) {
      yield put(estimatesActions.getCSV(csvId, callback))
    } else {
      yield call(toast.error, errorTextTryingTo('download csv estimates file'))
      yield call(callback, false)
    }
  } catch {
    yield call(toast.error, errorTextTryingTo('download csv estimates file'))
    yield call(callback, false)
  }
}

export function* getCSV({ payload, callback }: GetCSVEstimateAction): SagaIterator {
  try {
    const estimates = yield call(Punchlist.estimates.getCSV, payload)
    if (estimates.status === 200) {
      const csvUrl = estimates.data
      yield put(estimatesActions.setCSVEstimates(csvUrl))
      yield call(callback, true, csvUrl)
    } else {
      yield delay(1500);
      yield put(estimatesActions.getCSV(payload, callback))
    }
  } catch {
    yield call(toast.error, errorTextTryingTo('download csv estimates file'))
    yield call(callback, false)
  }
}

export function* assignMultipleEstimates({ payload, callback }: AssignMultipleEstimatesAction): SagaIterator {
  try {
    const estimates = yield call(Punchlist.estimates.assignMultipleEstimates, payload.estimateIds, payload.estimatorId)
    if (estimates) {
      yield call(callback, true)
    } else {
      yield call(toast.error, errorTextTryingTo('assign multiple estimates'))
      yield call(callback, false)
    }
  } catch {
    yield call(toast.error, errorTextTryingTo('assign multiple estimates'))
    yield call(callback, false)
  }
}

export default function* saga(): SagaIterator {
  yield takeLatest(estimatesTypes.FETCH_ESTIMATES, fetchEstimates)
  yield takeLatest(estimatesTypes.FETCH_RELATED_ESTIMATES, fetchRelatedEstimates)
  yield takeLatest(estimatesTypes.UPDATE_ESTIMATE, updateEstimate)
  yield takeLatest(estimatesTypes.FETCH_ESTIMATE_COUNTS, fetchEstimatesCounts)
  yield takeLatest(estimatesTypes.RELEASE_ESTIMATE, releaseEstimate)
  yield takeLatest(estimatesTypes.DOWNLOAD_ESTIMATE_CSV, downloadCSVEstimate)
  yield takeLatest(estimatesTypes.GET_ESTIMATES_CSV, getCSV)
  yield takeLatest(estimatesTypes.ASSIGN_MULTIPLE_ESTIMATES, assignMultipleEstimates)
}
